import React, { useState, useContext } from 'react';
import { uid } from 'react-uid';
import Img from 'gatsby-image';
import { graphql } from 'gatsby';
import { useSpring, animated } from 'react-spring';
import useMeasure from '../../../hooks/useMeasure';
import useResponsive from '../../../hooks/useResponsive';
import GlobalContext from '../../../context/global';
import SendGridLink from '../../SendGridLink';
import {
  StyledContainer,
  StyledMenu,
  StyledTitle,
  StyledMenuItem,
  StyledSubMenu,
  StyledSubMenuItem,
} from './styles';

const AnimatedMenu = animated(StyledMenu);
const AnimatedSubMenu = animated(StyledSubMenu);

const doesUrlMatchLocation = (url, location) =>
  url.endsWith(location.pathname) ||
  url.endsWith(location.pathname.slice(1, -1));

const SubMenu = ({ menu, isLast, isMobile }) => {
  const { location } = useContext(GlobalContext);
  const [ref, { height }] = useMeasure();
  const [isOpen, setOpen] = useState();
  const accordionSpring = useSpring({ height: isOpen ? height : 0 });
  const dropdownSpring = useSpring({
    height: isOpen ? height : 0,
    opacity: isOpen ? 1 : 0,
  });
  return (
    <>
      <StyledMenuItem
        isOpen={isOpen}
        isActive={menu.links.some((link) =>
          doesUrlMatchLocation(link.url, location)
        )}
      >
        <button type="button" onClick={() => setOpen(!isOpen)}>
          {menu.label}
        </button>
      </StyledMenuItem>
      <AnimatedSubMenu
        isLast={isLast}
        style={isMobile ? { ...accordionSpring } : { ...dropdownSpring }}
      >
        <div ref={ref}>
          <ul>
            {menu.links.map((link) => (
              <StyledSubMenuItem
                isActive={doesUrlMatchLocation(link.url, location)}
                key={uid(link)}
              >
                <SendGridLink
                  to={link.url}
                  dangerouslySetInnerHTML={{
                    __html: link.label,
                  }}
                />
              </StyledSubMenuItem>
            ))}
            {menu.additionalLink && (
              <StyledSubMenuItem isAdditional>
                <SendGridLink
                  to={menu.additionalLinkUrl}
                  dangerouslySetInnerHTML={{
                    __html: menu.additionalLinkLabel,
                  }}
                />
              </StyledSubMenuItem>
            )}
          </ul>
        </div>
      </AnimatedSubMenu>
    </>
  );
};

const Menu = ({ menu, overviewUri, pageUri }) => {
  const { location } = useContext(GlobalContext);
  const { isMobile } = useResponsive();
  const [ref, { height }] = useMeasure();
  const [isOpen, setOpen] = useState(false);
  const accordionSpring = useSpring({
    height: isOpen ? height : 0,
    marginBottom: isOpen ? height * -1 : 0,
  });

  const openMenu = (e) => {
    if (!isMobile) return;
    if (e.key && e.key !== 'Enter') return;
    setOpen(!isOpen);
  };

  return (
    <StyledContainer>
      <StyledTitle isMenuOpen={isOpen}>
        {menu.menuIcon && (
          <Img fluid={menu.menuIcon.imageFile.childImageSharp.fluid} />
        )}
        <figcaption
          role="button"
          aria-disabled={isMobile}
          onClick={openMenu}
          onKeyDown={openMenu}
        >
          <span dangerouslySetInnerHTML={{ __html: menu.menuTitle }} />
        </figcaption>
      </StyledTitle>

      <AnimatedMenu style={isMobile ? { ...accordionSpring } : undefined}>
        <div ref={ref}>
          <ul>
            <li>
              <StyledMenuItem isActive={Boolean(overviewUri === pageUri)}>
                <SendGridLink to="https://sendgrid.com/solutions/email-api/">Overview</SendGridLink>
              </StyledMenuItem>
            </li>
            {menu.menuItems.map((item, i) => {
              switch (item.fieldGroupName.split('_').pop()) {
                case 'Link':
                  return (
                    <li key={uid(item)}>
                      <StyledMenuItem
                        isActive={doesUrlMatchLocation(item.url, location)}
                      >
                        <SendGridLink
                          to={item.url}
                          dangerouslySetInnerHTML={{ __html: item.label }}
                        />
                      </StyledMenuItem>
                    </li>
                  );
                case 'Dropdown':
                  return (
                    <li key={uid(item)}>
                      <SubMenu
                        menu={item}
                        location={location}
                        isMobile={isMobile}
                        isLast={i === menu.menuItems.length - 1}
                      />
                    </li>
                  );
                default:
                  return null;
              }
            })}
          </ul>
        </div>
      </AnimatedMenu>
    </StyledContainer>
  );
};

export default Menu;

export const query = graphql`
  fragment UseCaseMenuFragment on WPGraphQL_UseCase {
    acfUseCasesNavigation {
      menuTitle
      menuIcon {
        sourceUrl
        imageFile {
          childImageSharp {
            fluid(maxWidth: 32, maxHeight: 32) {
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
      menuItems {
        ... on WPGraphQL_UseCase_Acfusecasesnavigation_MenuItems_Link {
          fieldGroupName
          label
          url
        }
        ... on WPGraphQL_UseCase_Acfusecasesnavigation_MenuItems_Dropdown {
          fieldGroupName
          label
          additionalLink
          additionalLinkLabel
          additionalLinkUrl
          links {
            label
            url
          }
        }
      }
    }
  }
`;
